/**
 * Load recovery — UI unlock helpers + optional bounded hydrate for load path.
 * Automatic heal/restore lives in recoveryPipeline / storageHealOrchestrator (PR5).
 * Prefer unlock* helpers here; call ensureEntityHydrateBounded only when the
 * recovery pipeline has not already hydrated this business in the same boot.
 */

import { logAchatsReload } from '../../utils/achatReloadDebug';
import { markBootGate } from '../../infrastructure/telemetry/bootTelemetry';
import { isDeferEntityHydrateOnBootEnabled } from '../../infrastructure/startup/bootFeatureFlags';
import {
  EMERGENCY_FIELD_READ_MS,
  EMERGENCY_SHELL_LOAD_MS,
  ENTITY_COUNT_DEADLINE_MS,
  ENTITY_HYDRATE_AWAIT_MS,
  batchHasUsablePosShell,
  buildEmptyBusinessBatch,
  runSoft,
  warnLoad,
  withDeadline
} from './businessLoadSupport';
import {
  BOOT_SHELL_FIELDS,
  SHELL_ONLY_FIELDS,
  LAZY_BOOT_PRIORITY_FIELDS,
  applyBatchFieldsToSetters,
  importPriorityEntityReaders,
  initializeInstantCacheFromBatch,
  mergeCanonicalFields,
  readEntityFieldsParallel
} from './businessLoadPhases';

const SHELL_FIELDS = [...BOOT_SHELL_FIELDS, ...SHELL_ONLY_FIELDS];
const EMPTY_FIELDS = [...SHELL_FIELDS, ...LAZY_BOOT_PRIORITY_FIELDS];

function unlockOptions(setDataLoaded, setDataShellReady, startTransition) {
  return {
    startTransition,
    setDataLoaded,
    setDataShellReady,
    markDataLoaded: true,
    markDataShellReady: true,
    urgent: true
  };
}

export async function loadEmergencyShellBatch(businessId) {
  const batchData = buildEmptyBusinessBatch();
  const readers = await importPriorityEntityReaders();
  const shellFields = await readEntityFieldsParallel(
    businessId,
    BOOT_SHELL_FIELDS,
    readers,
    { deadlineMs: EMERGENCY_FIELD_READ_MS }
  );
  mergeCanonicalFields(batchData, shellFields, { businessId });
  return batchData;
}

/** @returns {Promise<object|null>} usable shell batch or null */
export async function tryEmergencyShellBatch(businessId, label) {
  const recovered = await runSoft(
    label,
    () =>
      withDeadline(loadEmergencyShellBatch(businessId), EMERGENCY_SHELL_LOAD_MS, label),
    null
  );
  return batchHasUsablePosShell(recovered) ? recovered : null;
}

export function unlockWithShellBatch({
  batchData,
  setters,
  setDataLoaded,
  setDataShellReady,
  startTransition,
  businessId
}) {
  applyBatchFieldsToSetters(
    batchData,
    setters,
    SHELL_FIELDS,
    unlockOptions(setDataLoaded, setDataShellReady, startTransition)
  );
  initializeInstantCacheFromBatch(batchData, businessId);
}

export function unlockWithEmptyBatch({
  setters,
  setDataLoaded,
  setDataShellReady,
  startTransition,
  businessId
}) {
  const emptyData = buildEmptyBusinessBatch();
  applyBatchFieldsToSetters(
    emptyData,
    setters,
    EMPTY_FIELDS,
    unlockOptions(setDataLoaded, setDataShellReady, startTransition)
  );
  initializeInstantCacheFromBatch(emptyData, businessId);
}

/** Recover shell from entity stores, else empty defaults. Always unlocks UI. */
export async function unlockRecoveredOrEmpty({
  businessId,
  setters,
  setDataLoaded,
  setDataShellReady,
  startTransition,
  gate,
  emergencyLabel,
  reason
}) {
  const unlockCtx = {
    setters,
    setDataLoaded,
    setDataShellReady,
    startTransition,
    businessId
  };

  const recovered = await tryEmergencyShellBatch(businessId, emergencyLabel);
  if (recovered) {
    markBootGate(gate.recovered, {
      productsCount: recovered.products?.length ?? 0,
      reason
    });
    unlockWithShellBatch({ ...unlockCtx, batchData: recovered });
    return { recovered: true, batchData: recovered };
  }

  markBootGate(gate.empty, { reason });
  unlockWithEmptyBatch(unlockCtx);
  return { recovered: false, batchData: null };
}

export async function finalizeBusinessLoadUnlock({
  businessId,
  setReady,
  setDataShellReady
}) {
  try {
    if (typeof setReady === 'function') setReady(true);
    if (typeof setDataShellReady === 'function') setDataShellReady(true);
  } catch (err) {
    warnLoad('finalize ready flags', err);
  }

  try {
    const { dispatchBusinessLazyLoadComplete } = await import(
      '../operations/dailyClosingOrchestrator'
    );
    dispatchBusinessLazyLoadComplete(businessId);
  } catch (err) {
    warnLoad('finalize lazy-load complete dispatch', err);
  }
}

/** Count + hydrate entity stores with budgets; never hang Phase-1 forever. */
export async function ensureEntityHydrateBounded(businessId) {
  const runEntityHydrate = () =>
    runSoft(
      'entity hydrate before load',
      async () => {
        const { ensureEntityStoresHydratedBeforeLoad } = await import(
          '../../infrastructure/storage/entityStoreMigration'
        );
        const hydrate = await ensureEntityStoresHydratedBeforeLoad(businessId);
        logAchatsReload('load.entityHydrate', { businessId, ...hydrate });
        return hydrate;
      },
      null
    );

  const counts = await runSoft(
    'entity store count',
    async () => {
      const { countEntityStoresByType } = await import(
        '../../infrastructure/storage/entityAggregateStore'
      );
      return withDeadline(
        countEntityStoresByType(businessId),
        ENTITY_COUNT_DEADLINE_MS,
        'entity_store_count'
      );
    },
    null
  );

  const META_ONLY = new Set([
    'businessInfo',
    'userInfo',
    'metadata',
    'reports',
    'stockData',
    'discrepancyManagement',
    'stockLocations'
  ]);
  let entityTotal = 0;
  let entityDataTotal = 0;
  for (const [key, n] of Object.entries(counts || {})) {
    const c = Number(n || 0);
    entityTotal += c;
    if (!META_ONLY.has(key)) entityDataTotal += c;
  }

  // Meta-only shell still needs await hydrate (blob→entity) or UI stays empty
  const mustAwaitHydrate =
    entityDataTotal === 0 || !isDeferEntityHydrateOnBootEnabled();

  if (!mustAwaitHydrate) {
    markBootGate('entity_hydrate_deferred', { entityTotal });
    void runEntityHydrate();
    return { entityTotal, awaited: false, timedOut: false };
  }

  markBootGate('entity_hydrate_awaited', { entityTotal });
  try {
    await withDeadline(
      runEntityHydrate(),
      ENTITY_HYDRATE_AWAIT_MS,
      'entity_hydrate_before_load'
    );
    return { entityTotal, awaited: true, timedOut: false };
  } catch (hydrateBudgetErr) {
    warnLoad('entity hydrate budget exceeded — Phase-1 continues', hydrateBudgetErr);
    markBootGate('entity_hydrate_timeout', {
      entityTotal,
      error: hydrateBudgetErr?.message || String(hydrateBudgetErr)
    });
    void runEntityHydrate();
    return { entityTotal, awaited: true, timedOut: true };
  }
}
